import { useSignal, useSignalEffect } from '@preact/signals-react';
import dot from 'dot-object';
import { isEqual } from 'lodash';
import type { Ref } from 'react';
import { useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Observable, Subject, takeUntil } from 'rxjs';
import type { JsonDataWrapper } from '../../../common/data-retrieval/data-retrieval-interfaces';
import { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import { cn } from '../../../common/functions/cn';
import useTestTag from '../../../common/hooks/use-test-tag';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { forceAssert } from '../../common-functions';
import type { TypeOrArray } from '../../common-interfaces';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import type { DropDownOptionRendererRef } from '../../data/drop-down-option-renderer/drop-down-option-renderer.component';
import type { DynamicFieldComponentProps, DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { DataSearchLocation } from '../../enums';
import type { JsonData, JsonDataItem } from '../../generic-types';
import EmptyFieldLabel from '../../labels/empty-field-label';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import Loader from '../../loader/loader.component';
import OdinIcon from '../../odin-icon/odin-icon.component';
import type { DropDownNoSelection, DropDownResult } from '../drop-down/drop-down-types';
import ListBox from '../list-box/list-box.component';
import type { TwinColumnSelectState } from './twin-column-select.reducer';
import reducer, { TwinColumnSelectActionType } from './twin-column-select.reducer';

const moveButtonStyles = cn(
  'flex cursor-pointer items-center justify-center rounded-sm text-primary focus-visible:ring-2 focus-visible:ring-ring/50'
);

export interface TwinColumnSelectProps
  extends DynamicFieldComponentProps<Array<DropDownResult>>,
    DynamicFieldDataProps {
  ref?: Ref<TwinColumnSelectRef>;
  filterLocation?: DataSearchLocation;
  subType?: string;
  allowSearch?: boolean;
}

export interface TwinColumnSelectRef {
  clearData: () => void;
  addToSource: (data: DropDownResult) => void;
  retrieveValues: () => Array<DropDownResult>;
  clearCache: () => void;
  clearOptionValues: () => void;
}

function TwinColumnSelect(props: TwinColumnSelectProps) {
  const {
    ref,
    id,
    value,
    data,
    textField,
    valueField,
    pageSize = 50,
    label,
    showAddNewOption = false,
    filterLocation,
    readOnly = false,
    required,
    error,
    onChange,
    labelStyles,
    subType,
    allowSearch = true,
  } = props;

  // Ensure value is always an array
  const safeValue = Array.isArray(value) ? value : [];

  const availableSelections = useRef<Array<DropDownResult> | undefined>(undefined);
  const selectedSelections = useRef<Array<DropDownResult> | undefined>(undefined);
  const filteredSelectedSelections = useRef<Array<DropDownResult> | undefined>(undefined);
  const dataResult = useRef<Observable<JsonDataWrapper> | undefined>(undefined);
  const dataResultSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const destroySubject = useRef<Subject<void> | undefined>(undefined);
  const selectedValueRef = useRef<Array<DropDownResult>>(safeValue);
  const scrollerRef = useRef<DropDownOptionRendererRef>(null);

  const [showAddNewButton, setShowAddNewButton] = useState(false);

  const filterSignal = useSignal<Array<ContinuousScrollerFilter>>([]);
  const selectedFilterSignal = useSignal<Array<ContinuousScrollerFilter>>([]);

  const tagCreator = useTestTag(label || 'twin-column');
  const { t } = useTranslation();

  const initialValues: TwinColumnSelectState = {
    sourceData: data,
    sourceDataToRemove: safeValue,
    sourceValue: [],
    selectedData: safeValue,
    isLoading: false,
    textField,
    valueField,
  };

  const [state, dispatch] = useReducer(reducer, initialValues);

  useSignalEffect(() => {
    destroySubject.current = new Subject<void>();
    dataResult.current = new Observable<JsonDataWrapper>((subscriber) => {
      dataResultSubscriber.current = new OdinDataSender(subscriber);
    });
    dataResult.current?.pipe(takeUntil(destroySubject.current)).subscribe((dataWrapper) => {
      if (dataWrapper?.data) {
        const secondColumnData: JsonDataWrapper[] = [];
        if (filterSignal.value?.length > 0) {
          dataWrapper.data.forEach((item) => {
            const itemText: string = (item.text as string) ?? (item.caption as string) ?? '';
            if (itemText.toLowerCase().includes(filterSignal.value[0]?.value as string)) {
              secondColumnData.push(item);
            }
          });
        }
        dispatch({
          type: TwinColumnSelectActionType.GetAllDataEnd,
          allData: filterSignal.value?.length > 0 ? secondColumnData.map((item) => ({ ...item })) : dataWrapper.data,
          isFiltered: filterSignal.value.length > 0,
        });
      }
    });

    return () => {
      destroySubject.current?.next();
      destroySubject.current?.complete();
    };
  });

  useSignalEffect(() => {
    if (selectedFilterSignal.value.length > 0) {
      const filterValue = selectedFilterSignal.value[0].value as string;
      filteredSelectedSelections.current = state.selectedData.filter((item) =>
        (item.text as string).toLowerCase().includes(filterValue)
      );
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: notify parent when internal state changes, not when onChange changes
  useEffect(() => {
    if (state.selectedData.length !== selectedValueRef.current.length) {
      onChange?.(state.selectedData);
      selectedValueRef.current = state.selectedData;
    }
  }, [state.selectedData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentional: sync internal state from prop, not when internal state changes
  useEffect(() => {
    if (!isEqual(state.selectedData, safeValue)) {
      dispatch({
        type: TwinColumnSelectActionType.ResetState,
        state: initialValues,
      });

      selectedValueRef.current = safeValue;
    }
  }, [value]);

  const onChangeSource = (values?: TypeOrArray<DropDownResult> | DropDownNoSelection) => {
    if (Array.isArray(values)) {
      availableSelections.current = values;
    }
  };

  const onChangeSelected = (values?: TypeOrArray<DropDownResult> | DropDownNoSelection) => {
    if (Array.isArray(values)) {
      selectedSelections.current = values;
    }
  };

  const moveToSelected = () => {
    if (availableSelections.current) {
      dispatch({
        type: TwinColumnSelectActionType.MoveToSelected,
        selectedValues: availableSelections.current,
      });
    }
  };

  const removeFromSelected = () => {
    if (selectedSelections.current) {
      dispatch({
        type: TwinColumnSelectActionType.RemoveFromSelected,
        selectedValues: selectedSelections.current,
      });
    }
  };

  const moveAllToSelected = () => {
    if (data && !Array.isArray(data) && dataResultSubscriber.current) {
      data.getData(dataResultSubscriber.current, {
        getAllData: true,
        fieldType: 'TWIN_COLUMN_SELECT',
        fieldId: id,
        fieldSubType: subType,
      });
      dispatch({
        type: TwinColumnSelectActionType.GetAllDataStart,
      });
    }
  };

  const removeAllFromSelected = () => {
    dispatch({
      type: TwinColumnSelectActionType.RemoveAllFromSelected,
      selectedValues: filteredSelectedSelections.current,
    });
  };

  const onDoubleClickSource = (value: DropDownResult) => {
    dispatch({
      type: TwinColumnSelectActionType.DoubleClickSource,
      selectedValue: value,
    });
  };

  const onDoubleClickSelected = (value: DropDownResult) => {
    dispatch({
      type: TwinColumnSelectActionType.DoubleClickSelected,
      selectedValue: value,
    });
  };

  const dropDownDataLoaded = (page: number) => {
    if (page === 1 && showAddNewOption) {
      setShowAddNewButton(true);
    }
  };

  const clearData = () => {
    dispatch({
      type: TwinColumnSelectActionType.RemoveAllFromSelected,
    });
  };

  const addToSource = (data: DropDownResult) => {
    dispatch({
      type: TwinColumnSelectActionType.AddOptionToSource,
      selectedValue: data,
    });
  };

  const retrieveValues = () => {
    return state.selectedData;
  };

  const clearCache = () => {
    scrollerRef.current?.getDataOverwritePageCache();
  };

  const clearOptionValues = () => {
    scrollerRef.current?.resetDropDownData();
  };

  useImperativeHandle(ref, () => ({
    clearData,
    addToSource,
    retrieveValues,
    clearCache,
    clearOptionValues,
  }));

  const readOnlyElement = useMemo(
    () =>
      state.isLoading ? (
        <Loader />
      ) : (
        <ul className="list-none text-sm">
          {state.selectedData.map((item) => (
            <li key={item.text}>{item.text}</li>
          ))}
          {state.selectedData.length === 0 && <EmptyFieldLabel />}
        </ul>
      ),
    [state.isLoading, state.selectedData]
  );

  const onNewItemCreated = (value: JsonDataItem) => {
    const selectedValue: DropDownResult = {
      value: dot.pick(valueField, value),
      text: dot.pick(textField, value),
      originalData: value,
    };
    dispatch({
      type: TwinColumnSelectActionType.AddOptionToSource,
      selectedValue: selectedValue,
    });
  };

  return (
    <>
      {label && (
        <div className="flex">
          <FieldLabel label={label} labelStyles={labelStyles} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {readOnly && readOnlyElement}
      {!readOnly && (
        <div
          className={cn('flex flex-col items-start justify-between sm:flex-row', {
            'rounded-sm border border-error': error,
          })}
        >
          <div className="relative w-full min-w-0 grow" ref={tagCreator('left-column')}>
            {state.isLoading && <Loader useAsOverlay={true} />}
            <ListBox
              scrollerRef={scrollerRef}
              id={id}
              textField={textField}
              valueField={valueField}
              value={value}
              data={state.sourceData}
              filterLocation={filterLocation}
              onChange={onChangeSource}
              itemsToRemove={state.sourceDataToRemove}
              onDoubleClick={onDoubleClickSource}
              showAddNewOption={showAddNewButton}
              dropDownDataLoaded={dropDownDataLoaded}
              pageSize={pageSize}
              onNewItemCreated={onNewItemCreated}
              subType={subType}
              filterSignal={filterSignal}
              allowSearch={allowSearch}
            />
          </div>
          <div className="flex items-center justify-between gap-2 self-center px-2 py-4 sm:flex-col">
            <button
              type="button"
              onClick={moveToSelected}
              aria-label={t('move-to-selected')}
              className={moveButtonStyles}
              ref={tagCreator('move-to-selected-icon')}
            >
              <OdinIcon icon="ChevronRight" className="hidden sm:flex" />
              <OdinIcon icon="ChevronDown" className="sm:hidden" />
            </button>
            <button
              type="button"
              onClick={removeFromSelected}
              aria-label={t('remove-from-selected')}
              className={moveButtonStyles}
              ref={tagCreator('remove-from-selected-icon')}
            >
              <OdinIcon icon="ChevronLeft" className="hidden sm:flex" />
              <OdinIcon icon="ChevronUp" className="sm:hidden" />
            </button>
            <button
              type="button"
              onClick={moveAllToSelected}
              aria-label={t('move-all-to-selected')}
              className={moveButtonStyles}
              ref={tagCreator('move-all-selected-icon')}
            >
              <OdinIcon icon="ChevronsRight" className="hidden sm:flex" />
              <OdinIcon icon="ChevronsDown" className="sm:hidden" />
            </button>
            <button
              type="button"
              onClick={removeAllFromSelected}
              aria-label={t('remove-all-from-selected')}
              className={moveButtonStyles}
              ref={tagCreator('remove-all-selected-icon')}
            >
              <OdinIcon icon="ChevronsLeft" className="hidden sm:flex" />
              <OdinIcon icon="ChevronsUp" className="sm:hidden" />
            </button>
          </div>
          <div className="relative w-full min-w-0 grow" ref={tagCreator('right-column')}>
            {state.isLoading && <Loader useAsOverlay={true} />}
            <ListBox
              textField="text"
              valueField="value"
              data={forceAssert<JsonData>(state.selectedData)}
              onChange={onChangeSelected}
              onDoubleClick={onDoubleClickSelected}
              filterSignal={selectedFilterSignal}
              allowSearch={allowSearch}
            />
          </div>
        </div>
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
}

export default TwinColumnSelect;
